import type { EditedPage } from "@agent-ui/features/chat/utils/editedPages";

import React from "react";

import { ChatEditorContext } from "@agent-ui/features/chat/components/ReactEditorChat";
import { usePageTabsActions } from "@agent-ui/features/page-renderer/store/pageTabsStore";
import {
  useSandboxNavigate,
  useSandboxPreviewBaseUrl,
} from "@agent-ui/store/sandboxNavigationStore";
import { Button } from "@replo/design-system/components/button/Button";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import {
  ArrowUpRight,
  ChevronLeft,
  ChevronRight,
  CornerDownLeft,
} from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useRequiredContext } from "replo-utils/react/context";

export function EditedPagesCarousel({
  pages,
  isStreaming,
}: {
  pages: EditedPage[];
  isStreaming: boolean;
}) {
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const { openTab, clearTabStatus } = usePageTabsActions();
  const sandboxNavigate = useSandboxNavigate();
  const previewBaseUrl = useSandboxPreviewBaseUrl();
  const { editor } = useRequiredContext(ChatEditorContext);

  if (isStreaming || pages.length <= 1) {
    return null;
  }

  const handleOpenPage = (page: EditedPage) => {
    openTab({
      id: page.route,
      content: { type: "page", pageRoute: page.route, status: null },
    });
    clearTabStatus(page.route);
    sandboxNavigate(page.route);
  };

  const handleFollowup = (page: EditedPage) => {
    if (!editor) {
      return;
    }
    editor.commands.setContent(`Update ${page.route} to `);
    editor.commands.focus("end");
  };

  const currentPage = pages[currentIndex];
  if (!currentPage) {
    return null;
  }

  const previewUrl = previewBaseUrl
    ? `${previewBaseUrl}${currentPage.route === "/" ? "" : currentPage.route}`
    : undefined;

  return (
    <div className="flex flex-col gap-2 rounded-xl border-0.5 border-border bg-white p-3 shadow-button-selectable">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-default">Pages updated</span>
        {pages.length > 1 && (
          <div className="flex items-center gap-1">
            <Button
              icon
              variant="tertiary"
              size="sm"
              onClick={() => {
                setCurrentIndex((i) => (i - 1 + pages.length) % pages.length);
              }}
            >
              <ChevronLeft />
            </Button>
            <span className="text-xs text-muted tabular-nums">
              {currentIndex + 1}/{pages.length}
            </span>
            <Button
              icon
              variant="tertiary"
              size="sm"
              onClick={() => {
                setCurrentIndex((i) => (i + 1) % pages.length);
              }}
            >
              <ChevronRight />
            </Button>
          </div>
        )}
      </div>

      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={currentPage.route}
          className="group/page flex items-center gap-3 rounded-lg bg-sheet px-3 py-2.5"
          initial={{ opacity: 0, x: 6 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -6 }}
          transition={{ duration: 0.15, ease: "easeInOut" }}
        >
          {previewUrl && (
            <div className="relative h-10 w-16 shrink-0 overflow-hidden rounded border-0.5 border-border bg-white">
              <iframe
                src={previewUrl}
                sandbox="allow-same-origin allow-scripts"
                scrolling="no"
                className="pointer-events-none border-0"
                style={{
                  width: "1280px",
                  height: "800px",
                  transformOrigin: "top left",
                  transform: "scale(0.05)",
                }}
              />
            </div>
          )}
          <div className="flex flex-col min-w-0 flex-1">
            <span className="text-sm text-default truncate">
              {currentPage.route}
            </span>
          </div>
          <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover/page:opacity-100 transition-opacity duration-150">
            <Tooltip content="Request followup" triggerAsChild>
              <Button
                icon
                variant="tertiary"
                size="sm"
                onClick={() => handleFollowup(currentPage)}
              >
                <CornerDownLeft />
              </Button>
            </Tooltip>
            <Tooltip content="View page" triggerAsChild>
              <Button
                icon
                variant="tertiary"
                size="sm"
                onClick={() => handleOpenPage(currentPage)}
              >
                <ArrowUpRight />
              </Button>
            </Tooltip>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
